import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';

// v3: snapshots anteriores nao carregavam de forma confiavel is_pinned/pinned_at.
export const CONVERSATION_PREFETCH_CACHE_VERSION = 3;
export const CONVERSATION_PREFETCH_CACHE_TTL = 24 * 60 * 60 * 1000;
export const CONVERSATION_PREFETCH_CACHE_CONVERSATION_LIMIT = 10;
export const CONVERSATION_PREFETCH_CACHE_MESSAGE_LIMIT = 20;

export const filterServerValidatedConversations = conversations => {
  if (!Array.isArray(conversations)) {
    return [];
  }

  return conversations.filter(
    conversation => conversation?.prefetchSnapshot !== true
  );
};

const toSafeNumber = value => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getConversationRecency = conversation => {
  const recency =
    conversation?.last_activity_at ??
    conversation?.timestamp ??
    conversation?.updated_at ??
    conversation?.created_at ??
    0;
  const parsedRecency = Number(recency);

  return Number.isNaN(parsedRecency) ? 0 : parsedRecency;
};

const getConversationId = conversation => {
  const parsedId = Number(conversation?.id);
  return Number.isFinite(parsedId) ? parsedId : 0;
};

const getConversationMessages = conversation => {
  const messages = Array.isArray(conversation?.messages)
    ? conversation.messages
    : [];

  return messages
    .slice(-CONVERSATION_PREFETCH_CACHE_MESSAGE_LIMIT)
    .map(message => ({ ...message }));
};

const getConversationSnapshot = conversation => {
  const messages = getConversationMessages(conversation);

  if (!conversation?.id || !messages.length) {
    return null;
  }

  return {
    ...conversation,
    messages,
    dataFetched: true,
    conversationDetailsFetched: false,
    allMessagesLoaded: false,
    prefetchProgress: 100,
  };
};

export const buildConversationPrefetchScopeKey = ({
  accountId,
  userId,
} = {}) => {
  const normalizedAccountId = toSafeNumber(accountId);
  const normalizedUserId = toSafeNumber(userId);

  if (normalizedAccountId === null || normalizedUserId === null) {
    return null;
  }

  return `${normalizedAccountId}:${normalizedUserId}`;
};

export const buildConversationPrefetchSnapshot = ({
  accountId,
  userId,
  conversations = [],
} = {}) => {
  const scopeKey = buildConversationPrefetchScopeKey({ accountId, userId });

  if (!scopeKey) {
    return null;
  }

  const eligibleConversations = [...conversations]
    .filter(conversation => Array.isArray(conversation?.messages))
    .sort((left, right) => {
      return (
        getConversationRecency(right) - getConversationRecency(left) ||
        getConversationId(right) - getConversationId(left)
      );
    })
    .map(conversation => getConversationSnapshot(conversation))
    .filter(Boolean)
    .slice(0, CONVERSATION_PREFETCH_CACHE_CONVERSATION_LIMIT);

  if (!eligibleConversations.length) {
    return null;
  }

  return {
    version: CONVERSATION_PREFETCH_CACHE_VERSION,
    savedAt: Date.now(),
    accountId: toSafeNumber(accountId),
    userId: toSafeNumber(userId),
    conversations: eligibleConversations,
  };
};

export const persistConversationPrefetchSnapshot = payload => {
  const snapshot = buildConversationPrefetchSnapshot(payload);
  const scopeKey = buildConversationPrefetchScopeKey(payload);

  if (!scopeKey) {
    return null;
  }

  if (!snapshot) {
    LocalStorage.deleteFromJsonStore(
      LOCAL_STORAGE_KEYS.CONVERSATION_PREFETCH_CACHE,
      scopeKey
    );
    return null;
  }

  LocalStorage.updateJsonStore(
    LOCAL_STORAGE_KEYS.CONVERSATION_PREFETCH_CACHE,
    scopeKey,
    snapshot
  );

  return snapshot;
};

export const restoreConversationPrefetchSnapshot = ({
  accountId,
  userId,
} = {}) => {
  const scopeKey = buildConversationPrefetchScopeKey({ accountId, userId });

  if (!scopeKey) {
    return null;
  }

  const snapshot = LocalStorage.getFromJsonStore(
    LOCAL_STORAGE_KEYS.CONVERSATION_PREFETCH_CACHE,
    scopeKey
  );

  if (!snapshot || snapshot.version !== CONVERSATION_PREFETCH_CACHE_VERSION) {
    LocalStorage.deleteFromJsonStore(
      LOCAL_STORAGE_KEYS.CONVERSATION_PREFETCH_CACHE,
      scopeKey
    );
    return null;
  }

  const savedAt = Number(snapshot.savedAt);
  if (
    !Number.isFinite(savedAt) ||
    Date.now() - savedAt > CONVERSATION_PREFETCH_CACHE_TTL
  ) {
    LocalStorage.deleteFromJsonStore(
      LOCAL_STORAGE_KEYS.CONVERSATION_PREFETCH_CACHE,
      scopeKey
    );
    return null;
  }

  const conversations = Array.isArray(snapshot.conversations)
    ? snapshot.conversations
        .filter(conversation => Array.isArray(conversation?.messages))
        .map(conversation => ({
          ...conversation,
          messages: conversation.messages
            .slice(-CONVERSATION_PREFETCH_CACHE_MESSAGE_LIMIT)
            .map(message => ({ ...message })),
          dataFetched: true,
          conversationDetailsFetched: false,
          allMessagesLoaded: false,
          prefetchProgress: 100,
          prefetchSnapshot: true,
        }))
    : [];

  if (!conversations.length) {
    LocalStorage.deleteFromJsonStore(
      LOCAL_STORAGE_KEYS.CONVERSATION_PREFETCH_CACHE,
      scopeKey
    );
    return null;
  }

  return {
    ...snapshot,
    accountId: toSafeNumber(accountId),
    userId: toSafeNumber(userId),
    conversations,
  };
};

export const clearConversationPrefetchSnapshot = ({
  accountId,
  userId,
} = {}) => {
  const scopeKey = buildConversationPrefetchScopeKey({ accountId, userId });

  if (!scopeKey) {
    return;
  }

  LocalStorage.deleteFromJsonStore(
    LOCAL_STORAGE_KEYS.CONVERSATION_PREFETCH_CACHE,
    scopeKey
  );
};
